import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

type SignUpDatePickerProps = {
    value: string;
    placeholder: string;
    hasError?: boolean;
    maxDate?: Date;
    onChange: (value: string) => void;
};

const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const WEEK_DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const toDateValue = (date: Date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const formatDate = (value: string) => {
    if (!value) {
        return "";
    }

    return new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    }).format(new Date(`${value}T00:00:00`));
};

const SignUpDatePicker = ({
    value,
    placeholder,
    hasError = false,
    maxDate,
    onChange,
}: SignUpDatePickerProps) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const initialDate = value
        ? new Date(`${value}T00:00:00`)
        : (maxDate || new Date());
    const [isOpen, setIsOpen] = useState(false);
    const [displayMonth, setDisplayMonth] = useState(initialDate.getMonth());
    const [displayYear, setDisplayYear] = useState(initialDate.getFullYear());

    const years = useMemo(() => {
        const currentYear = (maxDate || new Date()).getFullYear();

        return Array.from({ length: 101 }, (_, index) => currentYear - index);
    }, [maxDate]);

    const calendarDays = useMemo(() => {
        const firstDay = new Date(displayYear, displayMonth, 1).getDay();
        const daysInMonth = new Date(displayYear, displayMonth + 1, 0).getDate();

        return Array.from({ length: 42 }, (_, index) => {
            const day = index - firstDay + 1;

            return day > 0 && day <= daysInMonth ? day : null;
        });
    }, [displayMonth, displayYear]);

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    const changeMonth = (direction: number) => {
        const nextDate = new Date(displayYear, displayMonth + direction, 1);
        setDisplayMonth(nextDate.getMonth());
        setDisplayYear(nextDate.getFullYear());
    };

    const handleDaySelect = (day: number) => {
        const selectedDate = new Date(displayYear, displayMonth, day);
        const limit = maxDate ? new Date(maxDate) : new Date();
        limit.setHours(23, 59, 59, 999);

        if (selectedDate > limit) {
            return;
        }

        onChange(toDateValue(selectedDate));
        setIsOpen(false);
    };

    return (
        <div ref={rootRef} className={`signup-date-picker ${isOpen ? "is-open" : ""}`}>
            <button
                type="button"
                className={`signup-date-trigger ${hasError ? "has-error" : ""}`}
                onClick={() => setIsOpen((current) => !current)}
            >
                <span className={value ? "has-value" : ""}>
                    {value ? formatDate(value) : placeholder}
                </span>
                <CalendarDays size={17} strokeWidth={1.8} />
            </button>

            {isOpen && (
                <div className="signup-calendar">
                    <div className="signup-calendar-header">
                        <button
                            type="button"
                            onClick={() => changeMonth(-1)}
                            aria-label="Previous month"
                        >
                            <ChevronLeft size={17} />
                        </button>

                        <div className="signup-calendar-month-year">
                            <select
                                value={displayMonth}
                                onChange={(event) => setDisplayMonth(Number(event.target.value))}
                            >
                                {MONTHS.map((month, index) => (
                                    <option key={month} value={index}>
                                        {month}
                                    </option>
                                ))}
                            </select>

                            <select
                                value={displayYear}
                                onChange={(event) => setDisplayYear(Number(event.target.value))}
                            >
                                {years.map((year) => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <button
                            type="button"
                            onClick={() => changeMonth(1)}
                            aria-label="Next month"
                        >
                            <ChevronRight size={17} />
                        </button>
                    </div>

                    <div className="signup-calendar-weekdays">
                        {WEEK_DAYS.map((day) => (
                            <span key={day}>{day}</span>
                        ))}
                    </div>

                    <div className="signup-calendar-grid">
                        {calendarDays.map((day, index) => {
                            if (!day) {
                                return <span key={`empty-${index}`} />;
                            }

                            const dateValue = toDateValue(
                                new Date(displayYear, displayMonth, day)
                            );
                            const isSelected = value === dateValue;
                            const isFuture =
                                new Date(displayYear, displayMonth, day) > new Date();

                            return (
                                <button
                                    key={dateValue}
                                    type="button"
                                    className={isSelected ? "is-selected" : ""}
                                    disabled={isFuture}
                                    onClick={() => handleDaySelect(day)}
                                >
                                    {day}
                                </button>
                            );
                        })}
                    </div>

                    <div className="signup-calendar-footer">
                        <button
                            type="button"
                            onClick={() => {
                                onChange("");
                                setIsOpen(false);
                            }}
                        >
                            Clear
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SignUpDatePicker;
